import Axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

// ═══ DEM Search (fluxo 2 — procurar/baixar tiles SRTM automaticamente) ═══

export interface DemTileBbox {
  south: number;
  north: number;
  west: number;
  east: number;
}

export interface DemTile {
  tile_id: string;
  bbox: DemTileBbox;
  center_lat: number;
  center_lon: number;
  distance_to_center_km: number;
  covers_facility: boolean;
  estimated_size_mb: number;
  dataset: string;
  already_cached: boolean;
}

export interface DemDomainCoverage {
  total_tiles_needed: number;
  tiles_shown: number;
  fully_covered: boolean;
}

export interface DemSearchResponse {
  tiles: DemTile[];
  domain_coverage: DemDomainCoverage;
}

export interface DemDownloadCreated {
  id: string;
  filename: string;
  source_type: "upload" | "opentopography" | "aws" | "cache";
  size_bytes: number | null;
}

export interface DemDownloadFailed {
  tile_id: string;
  error: string;
}

export interface DemDownloadResponse {
  created: DemDownloadCreated[];
  failed: DemDownloadFailed[];
}

/**
 * Extrai a mensagem de erro de uma resposta Axios de forma consistente
 * Verifica múltiplos formatos de resposta (Django REST, custom, etc.)
 */
function extractErrorMessage(error: AxiosError): string {
  const response = error.response;

  // Erro de rede (sem resposta do servidor)
  if (!response) {
    if (error.code === 'ECONNREFUSED') {
      return "Servidor indisponível. Verifique sua conexão.";
    }
    if (error.code === 'ETIMEDOUT' || error.code === 'TIMEOUT') {
      return "Tempo limite excedido. O servidor demorou para responder.";
    }
    if (error.message?.includes('Network Error')) {
      return "Erro de rede. Verifique sua conexão com a internet.";
    }
    return "Erro de conexão com o servidor.";
  }

  const data = response.data as any;

  // Verifica diferentes formatos de erro
  // 1. Formato custom do backend Aires: { error: "mensagem" }
  if (data?.error && typeof data.error === 'string') {
    return data.error;
  }

  // 2. Formato Django REST Framework: { detail: "mensagem" }
  if (data?.detail && typeof data.detail === 'string') {
    return data.detail;
  }

  // 3. Formato alternativo: { message: "mensagem" }
  if (data?.message && typeof data.message === 'string') {
    return data.message;
  }

  // 4. Array de erros do DRF: { field: ["erro1", "erro2"] }
  if (data && typeof data === 'object') {
    const firstKey = Object.keys(data)[0];
    if (firstKey && Array.isArray(data[firstKey])) {
      return `${firstKey}: ${data[firstKey][0]}`;
    }
  }

  // 5. Fallback para statusText HTTP
  if (response.statusText && response.statusText !== '') {
    return response.statusText;
  }

  // 6. Fallback baseado no código de status
  const statusMessages: Record<number, string> = {
    400: "Requisição inválida",
    401: "Sessão expirada - faça login novamente",
    403: "Acesso negado",
    404: "Recurso não encontrado",
    408: "Tempo limite excedido",
    500: "Erro interno do servidor",
    502: "Erro de gateway",
    503: "Serviço indisponível",
    504: "Tempo limite do gateway",
  };

  return statusMessages[response.status] || `Erro ${response.status}`;
}

/**
 * Verifica se é erro 401 e redireciona para login se necessário
 */
function handleUnauthorized(error: AxiosError): boolean {
  const response = error.response;
  if (response?.status === 401) {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.href = "/login";
    return true;
  }
  return false;
}

/**
 * Extrai o nome do arquivo do header Content-Disposition de uma resposta.
 * Aceita tanto aspas ("filename=\"x.xls\"") quanto formato RFC 5987
 * (filename*=UTF-8''x.xls). Se não encontrar, retorna o fallback.
 */
function extractFilenameFromResponse(headers: any, fallback: string): string {
  const cd: string | undefined = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  if (!cd) return fallback;

  // Tenta RFC 5987 (filename*=UTF-8''...)
  const rfc5987 = /filename\*\s*=\s*[^']*''([^;\s]+)/i.exec(cd);
  if (rfc5987) {
    try { return decodeURIComponent(rfc5987[1]); } catch { /* ignore */ }
  }
  // Tenta filename="..." ou filename=...
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  if (m && m[1]) return m[1].trim();
  return fallback;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export class Client {
  private axios!: AxiosInstance;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private refreshPromise: Promise<string> | null = null; // Mutex: single refresh at a time

  constructor(url: string, private getToken: () => string | undefined) {
    this.setup(url);
    this.startProactiveRefresh();
  }

  /** Proactive token refresh — renews every 90 minutes (before 2h expiry) */
  private startProactiveRefresh() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = setInterval(() => {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return;
      this.axios.post("/token/refresh/", { refresh: refreshToken })
        .then(({ data }) => {
          localStorage.setItem("access_token", data.access);
          if (data.refresh) localStorage.setItem("refresh_token", data.refresh);
        })
        .catch(() => {}); // Silent — reactive refresh will handle failures
    }, 90 * 60 * 1000); // 90 minutes
  }

  /** Exposes the axios instance for new services that manage their own endpoints */
  get api(): AxiosInstance {
    return this.axios;
  }

  setup(url: string) {
    const axios = Axios.create({
      baseURL: url,
    });

    axios.interceptors.request.use(this.intercept.bind(this));

    // Interceptor de resposta para refresh de token (com mutex para evitar refreshes simultâneos)
    axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Sem response = network error/timeout — não tratar como 401
        if (!error.response) {
          return Promise.reject(error);
        }

        const isRefreshRequest = originalRequest?.url?.includes('/token/refresh/');
        const alreadyRetried = originalRequest?._retry;

        if (
          error.response.status === 401 &&
          localStorage.getItem("refresh_token") &&
          !isRefreshRequest &&
          !alreadyRetried
        ) {
          originalRequest._retry = true;

          // Mutex: se já tem um refresh em andamento, esperar ele terminar
          if (!this.refreshPromise) {
            const refreshToken = localStorage.getItem("refresh_token");
            this.refreshPromise = this.axios
              .post("/token/refresh/", { refresh: refreshToken })
              .then(({ data }: AxiosResponse) => {
                localStorage.setItem("access_token", data.access);
                if (data.refresh) localStorage.setItem("refresh_token", data.refresh);
                this.refreshPromise = null;
                return data.access;
              })
              .catch((err: AxiosError) => {
                this.refreshPromise = null;
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                window.location.href = "/login";
                throw err;
              });
          }

          // Todas as requests 401 simultâneas esperam o mesmo refresh
          return this.refreshPromise.then((newToken: string) => {
            if (originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.axios(originalRequest);
            }
            return Promise.reject(error);
          });
        }

        // Se é requisição de refresh que falhou, limpar e redirecionar
        if (error.response.status === 401 && isRefreshRequest) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/login";
        }

        return Promise.reject(error);
      }
    );
    this.axios = axios;
  }

  private intercept = async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    try {
      const token = this.getToken();
      if (token) {
        // Only set Content-Type to JSON if not already FormData (multipart)
        if (!(config.data instanceof FormData)) {
          config.headers.set("Content-Type", "application/json");
        }
        config.headers.Authorization = `Bearer ${token}`;
        config.timeout = 0;
      }
      return config;
    } catch (e) {
      throw e;
    }
  };

  async login(username: string, password: string): Promise<any> {
    try {
      const { data } = await this.axios.post("/token/", {
        username,
        password,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // Login Microsoft (Entra): o back diz se está ligado e qual URL inicia o fluxo
  async getAuthConfig(): Promise<{ entra_enabled: boolean; entra_login_url: string | null }> {
    try {
      const { data } = await this.axios.get("/auth/config/");
      return data;
    } catch {
      return { entra_enabled: false, entra_login_url: null };
    }
  }

  // Troca o código de uso único devolvido pelo callback Microsoft pelo JWT
  async entraExchange(code: string): Promise<any> {
    try {
      const { data } = await this.axios.post("/auth/entra/token/", { code });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchFacilities(): Promise<any> {
    try {
      const { data } = await this.axios.get("/facilities/");
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async postFacility(facility: any): Promise<any> {
    try {
      const { data } = await this.axios.post("/facilities/", facility);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async createProject(project: any): Promise<any> {
    try {
      const { data } = await this.axios.post("/facilities/create-project/", project);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchSurfaceChars(facilityID: string): Promise<string> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/surface-chars/`);
      return data.surface_characteristics || "";
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchReceptors(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/receptors/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async updateReceptors(facilityID: string, receptors: string, useManualReceptors: boolean): Promise<any> {
    try {
      const { data } = await this.axios.put(`/facilities/${facilityID}/receptors/`, {
        receptors,
        use_manual_receptors: useManualReceptors,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchAutoReceptorPreview(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/receptors/auto-preview/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async parseDiscreteReceptorFile(facilityID: string, file: File): Promise<any> {
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await this.axios.post(
        `/facilities/${facilityID}/receptors/parse-file/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchReceptorAudit(params: { facility?: string; action?: string; user?: string; page?: number; page_size?: number } = {}): Promise<any> {
    try {
      const { data } = await this.axios.get('/facilities/receptor-audit/', { params });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchReceptorAuditBackup(logId: number): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/receptor-audit/${logId}/backup/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ═══ Terrain Config (AERMAP) ═══

  async fetchTerrainConfig(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/terrain-config/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchTerrainElevationGrid(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/terrain-config/elevation-grid/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async updateTerrainConfig(facilityID: string, config: any): Promise<any> {
    try {
      const { data } = await this.axios.put(`/facilities/${facilityID}/terrain-config/`, config);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async uploadTerrainDEM(facilityID: string, file: File, horizDatum: string = 'WGS84', vertDatum: string = 'EGM96'): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('horiz_datum', horizDatum);
      formData.append('vert_datum', vertDatum);
      const { data } = await this.axios.post(
        `/facilities/${facilityID}/terrain-config/dem-files/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchTerrainOutput(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/terrain-config/output/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async runAermap(facilityID: string, config?: any): Promise<any> {
    try {
      const { data } = await this.axios.post(`/facilities/${facilityID}/terrain-config/run-aermap/`, config || {});
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      // Em caso de 400 com relatório de preflight, propaga o body pro handler
      if (axiosError.response?.status === 400 && (axiosError.response?.data as any)?.preflight) {
        const err: any = new Error(extractErrorMessage(axiosError));
        err.preflight = (axiosError.response?.data as any).preflight;
        throw err;
      }
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async preflightAermap(facilityID: string, config?: any): Promise<any> {
    try {
      const { data } = await this.axios.post(`/facilities/${facilityID}/terrain-config/preflight-aermap/`, config || {});
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async previewAermapInp(facilityID: string, overrides?: any): Promise<{ success: boolean; aermap_input_text: string }> {
    try {
      const { data } = await this.axios.post(
        `/facilities/${facilityID}/terrain-config/preview-aermap-inp/`,
        overrides || {},
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchLastAermapJob(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/terrain-config/last-aermap-job/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteTerrainDEM(facilityID: string, fileId: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/facilities/${facilityID}/terrain-config/dem-files/${fileId}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async redetectTerrainDEM(facilityID: string, fileId: string): Promise<any> {
    try {
      const { data } = await this.axios.post(
        `/facilities/${facilityID}/terrain-config/dem-files/${fileId}/redetect/`,
        {},
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ═══ DEM Search (fluxo 2) ═══
  // Busca automática de tiles SRTM cobrindo o domínio da facility +
  // download via OpenTopography/AWS/cache. Ver
  // docs/DEM_SEARCH_IMPLEMENTATION_PLAN.md seção 5.4.

  async searchDems(facilityID: string): Promise<DemSearchResponse> {
    try {
      const { data } = await this.axios.get<DemSearchResponse>(
        `/facilities/${facilityID}/terrain-config/dem-search/`,
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async downloadDems(
    facilityID: string,
    tileIds: string[],
  ): Promise<DemDownloadResponse> {
    try {
      const { data } = await this.axios.post<DemDownloadResponse>(
        `/facilities/${facilityID}/terrain-config/dem-download/`,
        { tile_ids: tileIds },
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async patchFacility(facilityID: string, facility: any): Promise<any> {
    try {
      const { data } = await this.axios.patch(
        `/facilities/${facilityID}/`,
        facility
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteFacility(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/facilities/${facilityID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteFacilityPreview(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/delete-preview/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteFacilityCascade(facilityID: string, confirmationName: string): Promise<any> {
    try {
      const { data } = await this.axios.post(
        `/facilities/${facilityID}/delete-cascade/`,
        { confirmation_name: confirmationName },
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // Dedupe de requests em voo pra fetchEmissionSource: múltiplos componentes
  // (EmissionSource, MenuBody, SourcePathway) fazem fetch simultâneo da mesma
  // facility quando o usuário seleciona um projeto. Em facilities grandes
  // (PETRO REGAP com 186 fontes), isso dobrava o tempo de carregamento.
  // Se já existe uma Promise em voo pro mesmo facilityID, retorna ela.
  private _fetchEmissionSourceInflight: Map<string, Promise<any>> = new Map();

  async fetchEmissionSource(facilityID: string): Promise<any> {
    const inflight = this._fetchEmissionSourceInflight.get(facilityID);
    if (inflight) return inflight;

    const promise = (async () => {
      try {
        const { data } = await this.axios.get(
          "/facilities/" + facilityID + "/sources/"
        );
        return data;
      } catch (error) {
        const axiosError = error as AxiosError;
        handleUnauthorized(axiosError);
        throw new Error(extractErrorMessage(axiosError));
      } finally {
        this._fetchEmissionSourceInflight.delete(facilityID);
      }
    })();

    this._fetchEmissionSourceInflight.set(facilityID, promise);
    return promise;
  }

  async getMapConfig(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/facilities/${facilityID}/map-config/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 404) return null;
      console.warn('[getMapConfig] Erro inesperado:', extractErrorMessage(axiosError));
      return null;
    }
  }

  async saveMapConfig(facilityID: string, center_lat: number, center_lon: number, zoom: number): Promise<any> {
    const { data } = await this.axios.put(`/facilities/${facilityID}/map-config/`, { center_lat, center_lon, zoom });
    return data;
  }

  async resetMapConfig(facilityID: string): Promise<any> {
    const { data } = await this.axios.delete(`/facilities/${facilityID}/map-config/`);
    return data;
  }

  // ── Sync Control ──

  async toggleInventorySourceSync(inventoryId: string, sourceId: string, syncToFlow: boolean): Promise<any> {
    const { data } = await this.axios.patch(
      `/inventories/${inventoryId}/sources/${sourceId}/toggle-sync/`,
      { sync_to_flow: syncToFlow }
    );
    return data;
  }

  async getInventorySourceDeleteInfo(inventoryId: string, sourceId: string): Promise<any> {
    const { data } = await this.axios.get(
      `/inventories/${inventoryId}/sources/${sourceId}/delete-info/`
    );
    return data;
  }

  async getSourceDeleteInfo(sourceId: string): Promise<any> {
    const { data } = await this.axios.get(`/sources/${sourceId}/delete-info/`);
    return data;
  }

  async fetchStations(facilityID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(
        "/facilities/" + facilityID + "/stations/"
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async processAermet(facilityID: string, dateStart: string, dateEnd: string, avertime: string = "1", upperairSource?: string): Promise<any> {
    try {
      const { data } = await this.axios.post(
        "/facilities/" + facilityID + "/process-aermet/",
        { date_start: dateStart, date_end: dateEnd, avertime, upperair_source: upperairSource }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async postEmissionSource(emissionSource: any): Promise<any> {
    try {
      const { data } = await this.axios.post("/sources/", emissionSource);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getEmissionSource(emissionSourceID: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/sources/${emissionSourceID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async patchEmissionSource(
    emissionSourceID: string,
    emissionSource: any
  ): Promise<any> {
    try {
      const { data } = await this.axios.patch(
        `/sources/${emissionSourceID}/`,
        emissionSource
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteEmissionSource(emissionSourceID: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/sources/${emissionSourceID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchPlume(
    facilitiesID: string,
    sources: { sources: [] },
    startDate?: string,
    endDate?: string,
    avertime?: number | string,
    hours?: number,
    mode?: string,
    pflFileId?: string,
    sfcFileId?: string,
    // Lista opcional de SourceGroup IDs. Quando preenchida, o backend gera
    // INP com SRCGROUP custom (mantém SRCGROUP ALL pra consolidado).
    groupIds?: string[]
  ): Promise<any> {
    let url = "/facilities/" + facilitiesID + "/module-plumes/?";
    if (startDate) {
      url += "date_start=" + startDate + "&";
    }
    if (endDate) {
      url += "date_end=" + endDate + "&";
    }
    if (avertime) {
      url += "avertime=" + avertime;
    }
    if (hours) {
      url += "&hours=" + hours;
    }
    if (mode) {
      url += "&mode=" + mode;
    }
    if (pflFileId) {
      url += "&pfl_file_id=" + pflFileId;
    }
    if (sfcFileId) {
      url += "&sfc_file_id=" + sfcFileId;
    }

    // Backend já aceita campo opcional `group_ids` no body do ModuleSerializer.
    // Mesclamos com o objeto `sources` existente — fica `{sources: [...], group_ids: [...]}`
    // se groupIds vier; senão idêntico ao payload antigo.
    const body = groupIds && groupIds.length
      ? { ...sources, group_ids: groupIds }
      : sources;

    try {
      const { data } = await this.axios.post(url, body);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getPlumeDocs(plumeID: string): Promise<any> {
    try {
      const response = await this.axios.get(`/plumes/${plumeID}/plume-docs/`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plume-docs.zip");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchFacilityForecast(
    facilityID: string,
    period: string
  ): Promise<any> {
    try {
      const { data } = await this.axios.get(
        "/weather/facility/?facility=" + facilityID + "&period=" + period
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchAutoOccurrencesCount(plumeID: string): Promise<number> {
    try {
      const { data } = await this.axios.get(
        `/plumes/${plumeID}/auto-occurrences-count/`
      );
      return typeof data?.count === "number" ? data.count : 0;
    } catch (error) {
      // Endpoint não-crítico — silenciar pra não atrapalhar o fluxo da pluma
      return 0;
    }
  }

  async getOccurrence(facilityID: any): Promise<any> {
    try {
      const { data } = await this.axios.get(
        "/facilities/" + facilityID + "/occurrences/"
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async postOccurrence(occurrence: any): Promise<any> {
    try {
      const { data } = await this.axios.post("/occurrences/", occurrence);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getOccurrenceByID(occurrenceID: number): Promise<any> {
    try {
      const { data } = await this.axios.get(`/occurrences/${occurrenceID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async patchOccurrence(occurrenceID: number, occurrence: any): Promise<any> {
    try {
      const { data } = await this.axios.patch(
        `/occurrences/${occurrenceID}/`,
        occurrence
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteOccurrence(occurrenceID: number): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/occurrences/${occurrenceID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async postPlace(place: any): Promise<any> {
    try {
      const { data } = await this.axios.post("/places/", place);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getTypes(): Promise<any> {
    try {
      const { data } = await this.axios.get("/places/type/");
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async createType(name: string): Promise<any> {
    try {
      const { data } = await this.axios.post("/places/type/create/", { name });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getPlaces(facilityID: any): Promise<any> {
    try {
      const { data } = await this.axios.get(
        "/facilities/" + facilityID + "/places/"
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getPlacesByID(placeID: any): Promise<any> {
    try {
      const { data } = await this.axios.get(`/places/${placeID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deletePlace(placeID: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/places/${placeID}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async patchPlace(placeID: string, place: any): Promise<any> {
    try {
      const { data } = await this.axios.patch(`/places/${placeID}/`, place);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async resolveOccurrence(occurrenceID: number, occurrence: any): Promise<any> {
    try {
      const { data } = await this.axios.post(
        `/occurrences/${occurrenceID}/resolve/`,
        occurrence
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchOccurrenceDashboard(params?: {
    facility?: string;
    status?: string;
    intensity?: string;
    justification_type?: string;
    date_start?: string;
    date_end?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.facility) queryParams.append('facility', params.facility);
      if (params?.status !== undefined && params.status !== '') queryParams.append('status', params.status);
      if (params?.intensity !== undefined && params.intensity !== '') queryParams.append('intensity', params.intensity);
      if (params?.justification_type !== undefined && params.justification_type !== '')
        queryParams.append('justification_type', params.justification_type);
      if (params?.date_start) queryParams.append('date_start', params.date_start);
      if (params?.date_end) queryParams.append('date_end', params.date_end);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.page_size) queryParams.append('page_size', String(params.page_size));

      const url = `/occurrences/dashboard/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchOccurrenceSummary(params?: {
    facility?: string;
    date_start?: string;
    date_end?: string;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.facility) queryParams.append('facility', params.facility);
      if (params?.date_start) queryParams.append('date_start', params.date_start);
      if (params?.date_end) queryParams.append('date_end', params.date_end);

      const url = `/occurrences/dashboard/summary/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchPlumeAudit(params?: {
    facility?: string;
    is_forecast?: boolean;
    created_by?: string;
    date_start?: string;
    date_end?: string;
    page?: number;
    page_size?: number;
    record_type?: string;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.facility) queryParams.append('facility', params.facility);
      if (params?.is_forecast !== undefined) queryParams.append('is_forecast', String(params.is_forecast));
      if (params?.created_by) queryParams.append('created_by', params.created_by);
      if (params?.date_start) queryParams.append('date_start', params.date_start);
      if (params?.date_end) queryParams.append('date_end', params.date_end);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.page_size) queryParams.append('page_size', String(params.page_size));
      if (params?.record_type) queryParams.append('record_type', params.record_type);

      const url = `/plumes/audit/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchAuditSummary(params?: {
    facility?: string;
    date_start?: string;
    date_end?: string;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.facility) queryParams.append('facility', params.facility);
      if (params?.date_start) queryParams.append('date_start', params.date_start);
      if (params?.date_end) queryParams.append('date_end', params.date_end);

      const url = `/plumes/audit/summary/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchAuditProcessing(params?: {
    facility?: string;
    created_by?: string;
    date_start?: string;
    date_end?: string;
  }): Promise<{ count: number; results: any[] }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.facility) queryParams.append('facility', params.facility);
      if (params?.created_by) queryParams.append('created_by', params.created_by);
      if (params?.date_start) queryParams.append('date_start', params.date_start);
      if (params?.date_end) queryParams.append('date_end', params.date_end);

      const url = `/plumes/audit/processing/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async checkAuditPermission(): Promise<{ has_permission: boolean }> {
    try {
      const { data } = await this.axios.get('/plumes/audit/check-permission/');
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      return { has_permission: false };
    }
  }

  async downloadAuditFile(recordType: 'plume' | 'error', id: string): Promise<Blob> {
    try {
      const url = `/plumes/audit/download-${recordType}/${id}/`;
      const { data } = await this.axios.get(url, { responseType: 'blob' });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchPlumeResult(plumeId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/plumes/audit/view-plume/${plumeId}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchPlumeForOccurrence(plumeId: string): Promise<any> {
    // Mesma payload do fetchPlumeResult — apenas gate diferente (operador da
    // facility da pluma vs auditoria global). Usado pelo botão "Ver Pluma"
    // nos cards de auto-ocorrência.
    try {
      const { data } = await this.axios.get(`/plumes/${plumeId}/view/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ==================== Inventory ====================

  async fetchInventories(params?: { type?: string; search?: string }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.type) queryParams.append('type', params.type);
      if (params?.search) queryParams.append('search', params.search);
      const url = `/inventories/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async createInventory(inventory: any): Promise<any> {
    try {
      const { data } = await this.axios.post('/inventories/', inventory);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getInventory(id: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/inventories/${id}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async updateInventory(id: string, inventory: any): Promise<any> {
    try {
      const { data } = await this.axios.patch(`/inventories/${id}/`, inventory);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteInventory(id: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/inventories/${id}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchInventorySources(inventoryId: string, params?: { pollutant?: string; source_type?: string }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.pollutant) queryParams.append('pollutant', params.pollutant);
      if (params?.source_type) queryParams.append('source_type', params.source_type);
      const url = `/inventories/${inventoryId}/sources/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async addInventorySource(inventoryId: string, source: any): Promise<any> {
    try {
      const { data } = await this.axios.post(`/inventories/${inventoryId}/sources/add/`, source);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async editInventorySource(inventoryId: string, sourceId: string, source: any): Promise<any> {
    try {
      const { data } = await this.axios.patch(`/inventories/${inventoryId}/sources/${sourceId}/edit/`, source);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async removeInventorySource(inventoryId: string, sourceId: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/inventories/${inventoryId}/sources/${sourceId}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ── Emission Records ──

  async getEmissionRecords(inventoryId: string, sourceId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/inventories/${inventoryId}/sources/${sourceId}/emission-records/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async addEmissionRecord(inventoryId: string, sourceId: string, record: any): Promise<any> {
    try {
      const { data } = await this.axios.post(`/inventories/${inventoryId}/sources/${sourceId}/emission-records/add/`, record);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteEmissionRecord(inventoryId: string, sourceId: string, recordId: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/inventories/${inventoryId}/sources/${sourceId}/emission-records/${recordId}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ── Source Typology ──

  async getSourceTypology(inventoryId: string, sourceId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/inventories/${inventoryId}/sources/${sourceId}/typology/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 404) return null;
      console.warn('[getSourceTypology] Erro inesperado:', extractErrorMessage(axiosError));
      return null;
    }
  }

  async saveSourceTypology(inventoryId: string, sourceId: string, typology: any): Promise<any> {
    try {
      const { data } = await this.axios.put(`/inventories/${inventoryId}/sources/${sourceId}/typology/`, typology);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ── Emission Profiles ──

  async getEmissionProfiles(inventoryId: string, sourceId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/inventories/${inventoryId}/sources/${sourceId}/emission-profiles/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async addEmissionProfile(inventoryId: string, sourceId: string, profile: any): Promise<any> {
    try {
      const { data } = await this.axios.post(`/inventories/${inventoryId}/sources/${sourceId}/emission-profiles/add/`, profile);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteEmissionProfile(inventoryId: string, sourceId: string, profileId: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/inventories/${inventoryId}/sources/${sourceId}/emission-profiles/${profileId}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async importInventorySpreadsheet(inventoryId: string, file: File, format: string = 'SEMAD'): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('format', format);
      const { data } = await this.axios.post(`/inventories/${inventoryId}/import/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async importInventoryPreview(inventoryId: string, files: File[], options?: { format?: string; pollutant?: string; utm_zone?: number }): Promise<any> {
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (options?.format) formData.append('format', options.format);
      if (options?.pollutant) formData.append('pollutant', options.pollutant);
      if (options?.utm_zone) formData.append('utm_zone', String(options.utm_zone));
      const { data } = await this.axios.post(`/inventories/${inventoryId}/import/preview/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async importInventoryCommit(inventoryId: string, payload: any): Promise<any> {
    try {
      const { data } = await this.axios.post(`/inventories/${inventoryId}/import/commit/`, payload);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async exportInventoryExcel(inventoryId: string): Promise<void> {
    try {
      const response = await this.axios.get(`/inventories/${inventoryId}/export/excel/`, {
        responseType: 'blob',
      });
      const filename = extractFilenameFromResponse(response.headers, `inventario_${inventoryId}.xlsx`);
      triggerDownload(response.data, filename);
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchInventoryImports(inventoryId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/inventories/${inventoryId}/imports/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async exportInventoryAermod(inventoryId: string, pollutant?: string, layout?: "rows" | "columns"): Promise<void> {
    try {
      const qp = new URLSearchParams();
      if (pollutant) qp.append('pollutant', pollutant);
      if (layout) qp.append('layout', layout);
      const params = qp.toString() ? `?${qp.toString()}` : '';
      const response = await this.axios.get(
        `/inventories/${inventoryId}/export/aermod/${params}`,
        { responseType: "blob" }
      );
      const suffix = layout === 'columns' ? '_anexo' : '';
      const fallback = pollutant
        ? `${pollutant}_inventario${suffix}.xlsx`
        : `inventario_aermod${suffix}.xlsx`;
      const filename = extractFilenameFromResponse(response.headers, fallback);
      triggerDownload(response.data, filename);
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async sendInventoryToAermod(
    inventoryId: string,
    facilityId: string,
    sourceIds?: string[],
    pollutant?: string
  ): Promise<any> {
    try {
      const { data } = await this.axios.post(
        `/inventories/${inventoryId}/send-to-aermod/`,
        { facility_id: facilityId, source_ids: sourceIds, pollutant }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getOrCreateInventoryForFacility(facilityId: string): Promise<any> {
    try {
      const { data } = await this.axios.post(
        `/inventories/for-facility/`,
        { facility_id: facilityId }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async importFromFacility(inventoryId: string, facilityId: string): Promise<any> {
    try {
      const { data } = await this.axios.post(
        `/inventories/${inventoryId}/import-from-facility/`,
        { facility_id: facilityId }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async uploadModelingFile(
    inventoryId: string,
    file: File,
    fileType: 'PFL' | 'SFC' | 'SRTM',
    meta?: { description?: string; region_name?: string; date_start?: string; date_end?: string }
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", fileType);
      if (meta?.description) formData.append("description", meta.description);
      if (meta?.region_name) formData.append("region_name", meta.region_name);
      if (meta?.date_start) formData.append("date_start", meta.date_start);
      if (meta?.date_end) formData.append("date_end", meta.date_end);

      const { data } = await this.axios.post(
        `/inventories/${inventoryId}/upload-file/`,
        formData
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchModelingFiles(inventoryId: string, fileType?: string): Promise<any> {
    try {
      const params = fileType ? `?file_type=${fileType}` : '';
      const { data } = await this.axios.get(`/inventories/${inventoryId}/files/${params}`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async downloadModelingFile(inventoryId: string, fileId: string): Promise<string> {
    try {
      const { data } = await this.axios.get(`/inventories/${inventoryId}/files/${fileId}/download/`, { responseType: 'text' });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async editModelingFile(inventoryId: string, fileId: string, data: { file_name?: string; description?: string }): Promise<any> {
    try {
      const { data: resp } = await this.axios.patch(`/inventories/${inventoryId}/files/${fileId}/`, data);
      return resp;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async deleteModelingFile(inventoryId: string, fileId: string): Promise<any> {
    try {
      const { data } = await this.axios.delete(`/inventories/${inventoryId}/files/${fileId}/delete/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async generateDiagnosticPackage(
    inventoryId: string,
    params: {
      facility_id: string;
      pfl_file_id: string;
      sfc_file_id: string;
      date_start: string;
      date_end: string;
      avertime?: string;
      source_ids?: string[];
      pollutant?: string;
    }
  ): Promise<void> {
    try {
      const response = await this.axios.post(
        `/inventories/${inventoryId}/generate-package/`,
        params,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aermod_diag_${inventoryId}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async parseDiagnosticResults(
    inventoryId: string,
    posFile: File,
    zone: number,
    avertime?: number | string,
    isOdour?: boolean,
    sfcFile?: File,
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('pos_file', posFile);
      formData.append('zone', String(zone));
      if (avertime) formData.append('avertime', String(avertime));
      if (isOdour) formData.append('is_odour', 'true');
      if (sfcFile) formData.append('sfc_file', sfcFile);

      const { data } = await this.axios.post(
        `/inventories/${inventoryId}/parse-results/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async getOutputConfig(inventoryId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/inventories/${inventoryId}/output-config/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async saveOutputConfig(inventoryId: string, config: any): Promise<any> {
    try {
      const { data } = await this.axios.put(`/inventories/${inventoryId}/output-config/`, config);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  // ═══ Background Jobs (AERMOD Processing) ═══

  async fetchJobs(facilityId?: string): Promise<any> {
    try {
      let url = "/jobs/";
      if (facilityId) url += `?facility_id=${facilityId}`;
      const { data } = await this.axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async fetchJobStatus(jobId: string): Promise<any> {
    try {
      const { data } = await this.axios.get(`/jobs/${jobId}/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  async cancelJob(jobId: string): Promise<any> {
    try {
      const { data } = await this.axios.post(`/jobs/${jobId}/cancel/`);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      throw new Error(extractErrorMessage(axiosError));
    }
  }

  /**
   * Atualiza preferências do usuário logado. Espera o id no localStorage.
   * Envia PATCH /users/{id}/ com o subset de campos modificados.
   */
  async updateUserPreferences(prefs: { plume_animation_enabled?: boolean }): Promise<any> {
    try {
      const uid = localStorage.getItem("user_id");
      if (!uid) return null;
      const { data } = await this.axios.patch(`/users/${uid}/`, prefs);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      handleUnauthorized(axiosError);
      // Prefs não são críticas — não explode o fluxo se falhar
      console.warn("Falha ao salvar preferência do usuário:", extractErrorMessage(axiosError));
      return null;
    }
  }
}
